/* =========================================
   VAPOR THEME — GLOBAL JS
   ========================================= */

(function () {
  'use strict';

  /* ---------- Utilities ---------- */
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const formatMoney = (cents, currency = 'GBP') => {
    return new Intl.NumberFormat(navigator.language || 'en-GB', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  /* ---------- Age gate ---------- */
  const AgeGate = {
    storageKey: 'vapor_age_verified',
    init() {
      const el = $('[data-age-gate]');
      if (!el) return;
      if (localStorage.getItem(this.storageKey) === '1') {
        el.remove();
        return;
      }
      document.body.classList.add('no-scroll');
      el.hidden = false;
      $('[data-age-yes]', el)?.addEventListener('click', () => {
        localStorage.setItem(this.storageKey, '1');
        document.body.classList.remove('no-scroll');
        el.remove();
      });
      $('[data-age-no]', el)?.addEventListener('click', () => {
        window.location.href = 'https://www.google.com';
      });
    },
  };

  /* ---------- Announcement slideshow ---------- */
  const AnnouncementSlider = {
    init() {
      const slides = $$('[data-announce-slide]');
      if (slides.length <= 1) return;
      let i = 0;
      slides[0].classList.add('is-active');
      setInterval(() => {
        slides[i].classList.remove('is-active');
        i = (i + 1) % slides.length;
        slides[i].classList.add('is-active');
      }, 4500);
    },
  };

  /* ---------- Hero slideshow ---------- */
  const HeroSlider = {
    init() {
      $$('[data-hero]').forEach((hero) => {
        const slides = $$('[data-hero-slide]', hero);
        const dots = $$('[data-hero-dot]', hero);
        if (slides.length <= 1) return;
        let idx = 0;
        let timer;

        const go = (n) => {
          slides[idx].classList.remove('is-active');
          dots[idx]?.classList.remove('is-active');
          idx = (n + slides.length) % slides.length;
          slides[idx].classList.add('is-active');
          dots[idx]?.classList.add('is-active');
        };

        const start = () => {
          timer = setInterval(() => go(idx + 1), 6000);
        };
        const stop = () => clearInterval(timer);

        dots.forEach((dot, i) =>
          dot.addEventListener('click', () => {
            stop();
            go(i);
            start();
          })
        );

        hero.addEventListener('mouseenter', stop);
        hero.addEventListener('mouseleave', start);
        start();
      });
    },
  };

  /* ---------- Mobile menu ---------- */
  const MobileMenu = {
    init() {
      const toggle = $('[data-menu-toggle]');
      const nav = $('[data-nav-wrapper]');
      if (!toggle || !nav) return;
      toggle.addEventListener('click', () => {
        const open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        document.body.classList.toggle('no-scroll', open);
      });
    },
  };

  /* ---------- Tabs ---------- */
  const Tabs = {
    init() {
      $$('[data-tabs]').forEach((tabs) => {
        const btns = $$('[data-tab-target]', tabs);
        btns.forEach((btn) => {
          btn.addEventListener('click', () => {
            btns.forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            const targetId = btn.getAttribute('data-tab-target');
            const parent = tabs.closest('section, .section') || document;
            $$('.tabs__panel', parent).forEach((p) => {
              p.classList.toggle('is-active', p.id === targetId);
            });
          });
        });
      });
    },
  };

  /* ---------- Featured cards tabs ---------- */
  const FeaturedCardTabs = {
    init() {
      $$('[data-fc-tabs]').forEach((tabs) => {
        const section = tabs.closest('.featured-cards-section') || tabs.parentElement;
        const buttons = $$('[data-fc-tab]', tabs);
        const panels = $$('[data-fc-panel]', section);
        buttons.forEach((btn) => {
          btn.addEventListener('click', () => {
            const id = btn.getAttribute('data-fc-tab');
            buttons.forEach((b) => {
              const active = b === btn;
              b.classList.toggle('is-active', active);
              b.setAttribute('aria-selected', active ? 'true' : 'false');
            });
            panels.forEach((p) => {
              p.classList.toggle('is-active', p.getAttribute('data-fc-panel') === id);
            });
          });
        });
      });
    },
  };

  /* ---------- Product scroller ---------- */
  const Scroller = {
    init() {
      $$('[data-scroller]').forEach((s) => {
        const track = $('[data-scroller-track]', s);
        if (!track) return;
        const prev = $('[data-scroller-prev]', s);
        const next = $('[data-scroller-next]', s);

        const cardStep = () => {
          const firstCard = track.firstElementChild;
          if (!firstCard) return track.clientWidth * 0.8;
          const cardWidth = firstCard.getBoundingClientRect().width;
          const gap = parseFloat(getComputedStyle(track).columnGap || '0');
          return cardWidth + gap;
        };

        const updateButtons = () => {
          if (!prev || !next) return;
          const atStart = track.scrollLeft <= 4;
          const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
          prev.style.opacity = atStart ? '0.4' : '1';
          prev.style.pointerEvents = atStart ? 'none' : 'auto';
          next.style.opacity = atEnd ? '0.4' : '1';
          next.style.pointerEvents = atEnd ? 'none' : 'auto';
        };

        prev?.addEventListener('click', () =>
          track.scrollBy({ left: -cardStep(), behavior: 'smooth' })
        );
        next?.addEventListener('click', () =>
          track.scrollBy({ left: cardStep(), behavior: 'smooth' })
        );

        track.addEventListener('scroll', updateButtons, { passive: true });
        window.addEventListener('resize', updateButtons);
        updateButtons();
      });
    },
  };

  /* ---------- Cart drawer ---------- */
  const CartDrawer = {
    init() {
      this.drawer = $('[data-cart-drawer]');
      if (!this.drawer) return;

      $$('[data-cart-open]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );

      $$('[data-cart-close]', this.drawer).forEach((b) =>
        b.addEventListener('click', () => this.close())
      );
      $('.cart-drawer__overlay', this.drawer)?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
      });

      document.addEventListener('cart:updated', (e) => this.render(e.detail));

      $$('form[action$="/cart/add"]').forEach((form) => {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this.addToCart(new FormData(form));
        });
      });

      this.refresh();
    },

    async open() {
      await this.refresh();
      this.drawer.classList.add('is-open');
      document.body.classList.add('no-scroll');
    },

    close() {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('no-scroll');
    },

    async refresh() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this.render(cart);
      } catch (e) {
        console.warn('Cart fetch failed', e);
      }
    },

    async addToCart(formData) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!res.ok) throw new Error('Add failed');
        await this.refresh();
        this.open();
      } catch (e) {
        console.error('Add to cart failed', e);
      }
    },

    async updateQty(line, qty) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity: qty }),
        headers: { 'Content-Type': 'application/json' },
      });
      const cart = await res.json();
      this.render(cart);
    },

    render(cart) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });

      const itemsEl = $('[data-cart-items]', this.drawer);
      const subtotalEl = $('[data-cart-subtotal]', this.drawer);
      const progressEl = $('[data-cart-progress]', this.drawer);
      const progressFill = $('[data-cart-progress-fill]', this.drawer);
      const progressText = $('[data-cart-progress-text]', this.drawer);

      if (!itemsEl) return;

      if (cart.item_count === 0) {
        itemsEl.innerHTML = `<div class="cart-drawer__empty">
          <p>Your basket is empty</p>
          <a href="/collections/all" class="btn btn--primary" data-cart-close>Start shopping</a>
        </div>`;
        if (subtotalEl) subtotalEl.textContent = formatMoney(0, cart.currency);
        if (progressEl) progressEl.hidden = true;
        return;
      }

      itemsEl.innerHTML = cart.items
        .map(
          (item, i) => `
        <div class="cart-item" data-line="${i + 1}">
          <img src="${item.image || ''}" alt="${item.product_title}" class="cart-item__image" loading="lazy">
          <div>
            <p class="cart-item__title">${item.product_title}</p>
            ${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}
            <div class="cart-item__qty">
              <button type="button" data-qty-down aria-label="Decrease">-</button>
              <input type="number" value="${item.quantity}" min="0" data-qty-input>
              <button type="button" data-qty-up aria-label="Increase">+</button>
            </div>
          </div>
          <div>
            <p class="cart-item__price">${formatMoney(item.final_line_price, cart.currency)}</p>
            <button type="button" class="cart-item__remove" data-qty-remove>Remove</button>
          </div>
        </div>
      `
        )
        .join('');

      $$('[data-line]', itemsEl).forEach((row) => {
        const line = parseInt(row.dataset.line, 10);
        const input = $('[data-qty-input]', row);
        $('[data-qty-down]', row).addEventListener('click', () =>
          this.updateQty(line, Math.max(0, parseInt(input.value, 10) - 1))
        );
        $('[data-qty-up]', row).addEventListener('click', () =>
          this.updateQty(line, parseInt(input.value, 10) + 1)
        );
        $('[data-qty-remove]', row).addEventListener('click', () => this.updateQty(line, 0));
        input.addEventListener('change', () => this.updateQty(line, parseInt(input.value, 10) || 0));
      });

      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price, cart.currency);

      if (progressEl && window.VAPOR_CART_THRESHOLD) {
        const threshold = window.VAPOR_CART_THRESHOLD * 100;
        const pct = Math.min(100, (cart.total_price / threshold) * 100);
        progressEl.hidden = false;
        progressFill.style.width = `${pct}%`;
        if (cart.total_price >= threshold) {
          progressText.textContent = "You have qualified for free shipping!";
        } else {
          const remaining = threshold - cart.total_price;
          progressText.textContent = `You are ${formatMoney(remaining, cart.currency)} away from free shipping`;
        }
      }
    },
  };

  /* ---------- Search modal ---------- */
  const SearchModal = {
    init() {
      const modal = $('[data-search-modal]');
      if (!modal) return;

      $$('[data-search-open]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          modal.classList.add('is-open');
          document.body.classList.add('no-scroll');
          $('input[type="search"]', modal)?.focus();
        })
      );

      $$('[data-search-close]', modal).forEach((b) =>
        b.addEventListener('click', () => {
          modal.classList.remove('is-open');
          document.body.classList.remove('no-scroll');
        })
      );

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          modal.classList.remove('is-open');
          document.body.classList.remove('no-scroll');
        }
      });
    },
  };

  /* ---------- Mobile nav toggles ---------- */
  const MobileNavToggles = {
    init() {
      $$('[data-nav-toggle]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const parent = btn.closest('.nav__item--has-dropdown, .has-children');
          if (!parent) return;
          const isOpen = parent.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      });
    },
  };
  /* ---------- Sticky header shadow ---------- */
  const StickyHeader = {
    init() {
      const header = $('.header-wrapper');
      if (!header) return;
      const onScroll = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 10);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    },
  };

  /* ---------- Init ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    AgeGate.init();
    AnnouncementSlider.init();
    HeroSlider.init();
    MobileMenu.init();
    Tabs.init();
    FeaturedCardTabs.init();
    Scroller.init();
    MobileNavToggles.init();
    StickyHeader.init();
    CartDrawer.init();
    SearchModal.init();
  });
})();